import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";

import { apiGet, apiPost } from "../../app/api";
import { compactDateTime, dateDaysFromNow, formatNumber, formatQuantity, locationLabel } from "../../display";
import type { Location, LocationsPayload, ViewKey } from "../../types";
import { StandardDataTable, type StandardColumn } from "../../components/StandardDataTable";

type DailyInventoryRow = {
  row_type?: string;
  item?: string;
  inventory_name?: string;
  quantity_on_hand?: number;
  prod_date?: string;
  matched?: boolean;
};

type DailyInventoryPayload = {
  location?: Location | null;
  generated_at?: string;
  rows?: DailyInventoryRow[];
};

type AllBranchesInventoryRow = {
  row_type?: string;
  item?: string;
  matched?: boolean;
  branches?: Array<{ code?: string; quantity_on_hand?: number; prod_date?: string }>;
};

type AllBranchesInventoryPayload = {
  generated_at?: string;
  branches?: Location[];
  rows?: AllBranchesInventoryRow[];
};

type ProductSalesRow = {
  product?: string;
  quantity_sold?: number;
  receipt_count?: number;
  branches?: Array<{ branch?: string; branch_code?: string; quantity_sold?: number; receipt_count?: number }>;
};

function reportTitle(view: ViewKey): { title: string; description: string } {
  if (view === "report_all_branches_inventory") {
    return {
      title: "All Branches Inventory",
      description: "Review current branch balances in one report view."
    };
  }
  if (view === "report_product_sales_count") {
    return {
      title: "Product Sales Count",
      description: "Review POS sales counts and receipt totals by item."
    };
  }
  return {
    title: "Daily Inventory",
    description: "Generate the branch daily inventory report for a selected location."
  };
}

function flattenBranchSummary(row: ProductSalesRow): string {
  const branches = row.branches || [];
  if (!branches.length) return "";
  return branches
    .slice(0, 6)
    .map(branch => `${branch.branch || branch.branch_code || "Branch"} ${formatQuantity(branch.quantity_sold)}`)
    .join("; ");
}

function flattenAllBranchQuantity(row: AllBranchesInventoryRow): string {
  return (row.branches || [])
    .map(branch => `${branch.code || ""}: ${formatQuantity(branch.quantity_on_hand)}`)
    .join(" | ");
}

export function ReportsWorkspace({ view, csrfToken }: { view: ViewKey; csrfToken: string }) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [startDate, setStartDate] = useState(() => dateDaysFromNow(0));
  const [endDate, setEndDate] = useState(() => dateDaysFromNow(0));
  const [dailyRows, setDailyRows] = useState<DailyInventoryRow[]>([]);
  const [allBranchRows, setAllBranchRows] = useState<AllBranchesInventoryRow[]>([]);
  const [productSalesRows, setProductSalesRows] = useState<ProductSalesRow[]>([]);
  const [generatedAt, setGeneratedAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const title = reportTitle(view);

  const loadLocations = useCallback(async () => {
    try {
      const payload = await apiGet<LocationsPayload>("/api/locations");
      const branchRows = (payload.locations || []).filter(location => String(location.location_type || "").toLowerCase() === "branch");
      setLocations(branchRows);
      setSelectedLocation(current => current || String(branchRows[0]?.id || ""));
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Report locations could not be loaded." });
    }
  }, []);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  const runDailyInventory = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiPost<DailyInventoryPayload>("/api/reports/daily-inventory", {
        location_id: selectedLocation
      }, csrfToken);
      setDailyRows(payload.rows || []);
      setGeneratedAt(payload.generated_at || "");
      const location = payload.location ? locationLabel(payload.location) : "selected branch";
      setMessage({ variant: "success", title: `Daily Inventory generated for ${location}.` });
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Daily Inventory could not be generated." });
    } finally {
      setLoading(false);
    }
  }, [csrfToken, selectedLocation]);

  const runAllBranches = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiPost<AllBranchesInventoryPayload>("/api/reports/all-branches-inventory", {}, csrfToken);
      setAllBranchRows(payload.rows || []);
      setGeneratedAt(payload.generated_at || "");
      setMessage({ variant: "success", title: `All Branches Inventory generated with ${formatNumber((payload.branches || []).length)} branch columns.` });
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "All Branches Inventory could not be generated." });
    } finally {
      setLoading(false);
    }
  }, [csrfToken]);

  const runProductSales = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiPost<{ rows?: ProductSalesRow[] }>("/api/reports/product-sales-count", {
        start_date: startDate,
        end_date: endDate
      }, csrfToken);
      setProductSalesRows(payload.rows || []);
      setGeneratedAt(new Date().toISOString());
      setMessage({ variant: "success", title: `Product Sales Count returned ${formatNumber((payload.rows || []).length)} product rows.` });
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Product Sales Count could not be generated." });
    } finally {
      setLoading(false);
    }
  }, [csrfToken, endDate, startDate]);

  useEffect(() => {
    setMessage(null);
    setGeneratedAt("");
    if (view === "report_daily_inventory") setDailyRows([]);
    if (view === "report_all_branches_inventory") setAllBranchRows([]);
    if (view === "report_product_sales_count") setProductSalesRows([]);
  }, [view]);

  const dailyColumns: StandardColumn<DailyInventoryRow>[] = useMemo(() => [
    { key: "item", label: "Item", value: row => row.item || "", render: row => row.row_type === "section" ? <strong>{row.item || ""}</strong> : row.item || "" },
    { key: "inventory_name", label: "Matched Inventory Item", value: row => row.inventory_name || "" },
    { key: "quantity_on_hand", label: "Quantity", value: row => row.quantity_on_hand ?? "", render: row => row.row_type === "section" ? "" : formatQuantity(row.quantity_on_hand), textCenter: true },
    { key: "prod_date", label: "Production Date", value: row => row.prod_date || "" },
    { key: "matched", label: "Match", value: row => row.matched === false ? "Unmatched" : "Matched" }
  ], []);

  const allBranchColumns: StandardColumn<AllBranchesInventoryRow>[] = useMemo(() => [
    { key: "item", label: "Item", value: row => row.item || "", render: row => row.row_type === "section" ? <strong>{row.item || ""}</strong> : row.item || "" },
    { key: "branches", label: "Branch Quantities", value: flattenAllBranchQuantity },
    { key: "matched", label: "Match", value: row => row.matched === false ? "Unmatched" : "Matched" }
  ], []);

  const productColumns: StandardColumn<ProductSalesRow>[] = useMemo(() => [
    { key: "product", label: "Product", value: row => row.product || "" },
    { key: "quantity_sold", label: "Quantity Sold", value: row => row.quantity_sold ?? 0, render: row => formatQuantity(row.quantity_sold), textCenter: true },
    { key: "receipt_count", label: "Receipts", value: row => row.receipt_count ?? 0, render: row => formatNumber(row.receipt_count || 0), textCenter: true },
    { key: "branches", label: "Branch Detail", value: flattenBranchSummary }
  ], []);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id={`pf-${view}-view`} className="pf-toj-report-workspace">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>{title.title}</h1>
            <p>{title.description}</p>
          </FlexItem>
          {loading && <FlexItem><Spinner size="md" aria-label={`Loading ${title.title}`} /></FlexItem>}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <Card className="pf-toj-work-card pf-toj-primary-work-card">
          <CardBody>
            {view === "report_daily_inventory" && (
              <Form className="pf-toj-report-controls">
                <FormGroup label="Branch">
                  <FormSelect aria-label="Daily Inventory branch" value={selectedLocation} onChange={(_event, value) => setSelectedLocation(String(value))}>
                    {locations.map(location => (
                      <FormSelectOption key={location.id} value={String(location.id)} label={locationLabel(location)} />
                    ))}
                  </FormSelect>
                </FormGroup>
                <Button variant="primary" onClick={() => void runDailyInventory()} isDisabled={loading || !selectedLocation}>
                  Run report
                </Button>
              </Form>
            )}
            {view === "report_all_branches_inventory" && (
              <div className="pf-toj-report-controls">
                <Button variant="primary" onClick={() => void runAllBranches()} isDisabled={loading}>
                  Run report
                </Button>
              </div>
            )}
            {view === "report_product_sales_count" && (
              <Form className="pf-toj-report-controls">
                <FormGroup label="Start date">
                  <TextInput aria-label="Product Sales Count start date" type="date" value={startDate} onChange={(_event, value) => setStartDate(String(value))} />
                </FormGroup>
                <FormGroup label="End date">
                  <TextInput aria-label="Product Sales Count end date" type="date" value={endDate} onChange={(_event, value) => setEndDate(String(value))} />
                </FormGroup>
                <Button variant="primary" onClick={() => void runProductSales()} isDisabled={loading}>
                  Run report
                </Button>
              </Form>
            )}
            {generatedAt && <p className="pf-toj-muted pf-toj-report-generated">Generated {compactDateTime(generatedAt)}</p>}
            {view === "report_daily_inventory" && (
              <StandardDataTable ariaLabel="Daily Inventory report rows" rows={dailyRows} columns={dailyColumns} searchPlaceholder="Search daily inventory rows" />
            )}
            {view === "report_all_branches_inventory" && (
              <StandardDataTable ariaLabel="All Branches Inventory report rows" rows={allBranchRows} columns={allBranchColumns} searchPlaceholder="Search all branches inventory rows" />
            )}
            {view === "report_product_sales_count" && (
              <StandardDataTable ariaLabel="Product Sales Count report rows" rows={productSalesRows} columns={productColumns} searchPlaceholder="Search product sales rows" />
            )}
          </CardBody>
        </Card>
      </div>
    </PageSection>
  );
}
